import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Alert as MuiAlert,
  CircularProgress,
} from '@mui/material';
import { useApi } from '../../hooks/useApi';
import {
  AlertSubscriptionStatusLabel,
  MANUAL_SUBSCRIPTION_STATUS_OPTIONS,
  type EthocaAlert,
} from '../../types/alerts';

interface SubscriptionStatusDialogProps {
  open: boolean;
  alert: EthocaAlert;
  onClose: () => void;
  onSave: () => void;
}

export default function SubscriptionStatusDialog({
  open,
  alert,
  onClose,
  onSave,
}: SubscriptionStatusDialogProps) {
  const { post, loading, error } = useApi();
  const [status, setStatus] = useState<string>(alert.subscription_status);
  const [notes, setNotes] = useState<string>(alert.cm_notes ?? '');

  const handleClose = (_event: unknown, reason?: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  const handleSubmit = async () => {
    if (loading) return;
    try {
      await post('/api/alerts/ethoca/subscription-status', {
        alertId: alert.alert_id,
        subscriptionStatus: status,
        notes: notes.trim() ? notes.trim() : undefined,
      });
      onSave();
    } catch (err) {
      console.error('Failed to update subscription status:', err);
    }
  };

  const isUnchanged = status === alert.subscription_status && (notes ?? '') === (alert.cm_notes ?? '');

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby="subscription-status-dialog-title">
      <DialogTitle id="subscription-status-dialog-title">
        Edit Subscription Status — Alert #{alert.ethoca_alert_id}
      </DialogTitle>
      <DialogContent>
        {error && (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {error}
          </MuiAlert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl size="small" fullWidth disabled={loading}>
            <InputLabel id="subscription-status-label">Subscription Status</InputLabel>
            <Select
              labelId="subscription-status-label"
              id="subscription-status"
              value={status}
              label="Subscription Status"
              onChange={(e) => setStatus(e.target.value)}
            >
              {MANUAL_SUBSCRIPTION_STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {AlertSubscriptionStatusLabel[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            maxRows={6}
            disabled={loading}
            placeholder="Optional context for this status change..."
            helperText="Reuses the alert's CM Notes field."
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || isUnchanged}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
